import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { hrRequests } from "../../api/hr";
import { listRequests as timeOffRequests } from "../../api/timeOff";
import { listBranches, listUsers } from "../../api/directory";
import { HistoryRow } from "./HistoryRow";
import { HistoryFilters } from "./HistoryFilters";
import { HistoryExportBar } from "./HistoryExportBar";
import { buildRows } from "./historyRows";

// الطلبات التي تنتظر قرار المدير تُعرض أولاً، ثم الباقي الأحدث فالأقدم.
const PENDING = new Set(["pending", "cancel_requested"]);

// سجل كل طلبات الموظفين للمدير مع فلاتر (فرع/موظف/شهر) + تصدير Excel/طباعة + موافقة فورية.
export const RequestsHistory = ({ kinds = null }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState("all");
  const [branch, setBranch] = useState("");
  const [employee, setEmployee] = useState("");
  const [month, setMonth] = useState("");
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    const [hr, off] = await Promise.all([hrRequests(status), timeOffRequests(status)]);
    const resumes = ["all", "pending"].includes(status) ? (await hrRequests("cancel_requested")).leaves || [] : [];
    setRows(buildRows({ hr, resumes, off, t }));
  }, [status, t]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    listBranches().then(setBranches).catch(() => {});
    listUsers().then(setUsers).catch(() => {});
  }, []);

  const months = useMemo(
    () => [...new Set(rows.map((r) => (r.date || "").slice(0, 7)).filter(Boolean))].sort().reverse(),
    [rows],
  );
  const filtered = rows
    .filter((r) =>
      (!kinds || kinds.includes(r.kind)) && (!branch || r.branch_id === branch) && (!employee || r.user_id === employee) && (!month || (r.date || "").startsWith(month)))
    .sort((a, b) => {
      const pa = PENDING.has(a.status) ? 0 : 1;
      const pb = PENDING.has(b.status) ? 0 : 1;
      return pa !== pb ? pa - pb : (b.date || "").localeCompare(a.date || "");
    });

  const headers = [
    { key: "date", label: t("requests.colDate") }, { key: "who", label: t("requests.colEmployee") },
    { key: "kindLabel", label: t("requests.colType") }, { key: "ref", label: t("requests.colRef") },
    { key: "title", label: t("requests.colDetail") }, { key: "statusLabel", label: t("requests.colStatus") },
  ];
  const title = t("requests.historyTitle");

  return (
    <div data-testid="requests-history" className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="label-xs text-base">{title}</p>
        <HistoryExportBar rows={filtered} headers={headers} title={title} />
      </div>
      <div className="mt-3">
        <HistoryFilters status={status} setStatus={setStatus} branch={branch} setBranch={setBranch}
          employee={employee} setEmployee={setEmployee} month={month} setMonth={setMonth}
          branches={branches} users={users} months={months} />
      </div>
      {!filtered.length ? (
        <p className="mt-4 text-base text-muted-foreground" data-testid="history-empty">{t("myRequests.empty")}</p>
      ) : (
        <ul className="mt-4 grid list-none gap-3">
          {filtered.map((row) => <HistoryRow key={`${row.kind}-${row.id}`} row={row} reload={load} />)}
        </ul>
      )}
    </div>
  );
};
